// src/data/musicData.js
export const INSTRUMENTATIONS = [
  "Any",
  "Band",
  "Big Band",
  "Brass",
  "Chamber Ensemble",
  "Choir",
  "Guitar",
  "Orchestra",
  "Percussion",
  "Piano",
  "String Quartet",
  "Symphonic Band",
  "Violin",
  "Voice",
  "Wind Ensemble",
  "Woodwinds",
  "Solo",
  "Duo",
  "Trio",
  "Quartet",
];

export const GENRES = [
  "Baroque",
  "Blues",
  "Classical",
  "Contemporary",
  "Country",
  "Electronic",
  "Etude",
  "Folk",
  "Funk",
  "Hip-Hop",
  "Jazz",
  "Pop",
  "R&B",
  "Rock",
  "Romantic",
  "Swing",
];

// List of popular composers for autocomplete
export const POPULAR_COMPOSERS = [
  "J.S. Bach",
  "Ludwig van Beethoven",
  "Wolfgang Amadeus Mozart",
  "Frédéric Chopin",
  "Claude Debussy",
  "Pyotr Ilyich Tchaikovsky",
  "Johannes Brahms",
  "George Frideric Handel",
  "Antonio Vivaldi",
  "Franz Schubert",
  "Felix Mendelssohn",
  "Sergei Rachmaninoff",
  "Robert Schumann",
  "Franz Liszt",
  "Joseph Haydn",
  "Igor Stravinsky",
  "Maurice Ravel",
  "Gustav Mahler",
  "Antonín Dvořák",
  "Edvard Grieg",
  "Sergei Prokofiev",
  "Dmitri Shostakovich",
  "Béla Bartók",
  "Richard Wagner",
  "Giuseppe Verdi",
  "Giacomo Puccini",
  "George Gershwin",
  "Samuel Barber",
  "Aaron Copland",
  "Philip Glass",
  "Steve Reich",
  "Arvo Pärt",
  "John Adams",
  "John Cage",
  "Pierre Boulez",
  "Karlheinz Stockhausen",
  "Charles Ives",
  "Leonard Bernstein",
  "Duke Ellington",
  "Charlie Parker",
  "Miles Davis",
  "John Coltrane",
  "Thelonious Monk",
  "Charles Mingus",
];

// List of common music tags for autocomplete
export const COMMON_TAGS = [
  "Syncopated",
  "Polyrhythm",
  "Mixed Meter",
  "Odd Meter",
  "Compound Time",
  "Simple Time",
  "Subdivisions",
  "Accent Pattern",
  "Improv",
  "Practice",
  "Exercise",
  "Warm-up",
  "Advanced",
  "Beginner",
  "Intermediate",
  "Microtonal",
  "Spectral",
  "Groove",
  "Straight",
  "Swing",
  "Shuffle",
  "Waltz",
  "Progressive",
  "Quintuplet",
  "Sextuplet",
  "Septuplet",
  "Tuplet",
  "Triplet",
  "Counterpoint",
  "Modal",
  "Atonal",
  "Tonal",
  "Lydian",
  "Dorian",
  "Mixolydian",
  "Phrygian",
  "Trombone",
  "Trumpet",
  "Saxophone",
  "Violin",
  "Cello",
  "Piano",
  "Guitar",
  "Bass",
  "Drums",
  "Percussion",
  "Viola",
  "Flute",
  "Oboe",
  "Clarinet",
  "Bassoon",
  "Horn",
  "Tuba",
];

// Practice plan focus areas — used by the library's Practice Plans sub-section.
export const PRACTICE_FOCUS_OPTIONS = [
  "Audition Prep",
  "Concert Prep",
  "Daily Routine",
  "Etudes",
  "Improvisation",
  "Intonation",
  "Memorization",
  "Recital Prep",
  "Repertoire",
  "Rhythm",
  "Scales & Arpeggios",
  "Sight-reading",
  "Solo Prep",
  "Technique",
  "Tone Development",
  "Warm-up",
];

// Bucketed plan durations (minutes) for library filtering.
export const PRACTICE_DURATION_OPTIONS = [
  { value: "short", label: "Short (< 30 min)", maxMinutes: 30 },
  { value: "medium", label: "Medium (30–60 min)", maxMinutes: 60 },
  { value: "long", label: "Long (60–120 min)", maxMinutes: 120 },
  { value: "extended", label: "Extended (2+ hours)", maxMinutes: Infinity },
];

// Bucket a total-minutes number into one of the duration option values above.
export function bucketPracticeDuration(totalMinutes) {
  const m = Number(totalMinutes) || 0;
  if (m < 30) return "short";
  if (m < 60) return "medium";
  if (m < 120) return "long";
  return "extended";
}

// For export:
// Map each FlowFrame "sound" to a MIDI note number and separate accent velocity.
export const SOUND_MAP = {
  clicker: { note: 60, baseVelocity: 70 }, // Middle C
  click: { note: 62, baseVelocity: 70 },
  wood: { note: 64, baseVelocity: 70 },
  cowbell: { note: 66, baseVelocity: 70 },
  hihat: { note: 68, baseVelocity: 70 },
};

// jsmidgen's default is 128 ticks = 1 quarter note
export const TICKS_PER_QUARTER = 128;
export const DEFAULT_TEMPO = 120;

/** 1) Constants & Helper Data */
export const INITIAL_TEMPO = 60;
export const INITIAL_MUTE_RANDOM = false;
export const INITIAL_MUTE_PERCENT = 50;
export const INITIAL_LOOP_MODE = "loop"; // or "continue"

export const DEFAULT_TEMPO_CHANGE = {
  type: "linear",           // Changed from "incremental" to "linear"
  startMeasure: 1,
  endMeasure: 4,
  startTempo: 60,
  endTempo: 120,           // Keep only one endTempo property
  noteValue: null,
};

/**
 * Subdivisions for metric modulation
 * e.g. from "Quarter note" → "Triplet quarter"
 */
export const ALL_SUBDIVISIONS = [
  { name: "Whole note", value: 1 },
  { name: "Dotted half note", value: 1.5 },
  { name: "Half note", value: 2 },
  { name: "Quarter note", value: 4 },
  { name: "Dotted quarter note", value: 2.66667 },
  { name: "Quintuplet quarter note", value: 5 },
  { name: "Dotted eighth note", value: 5.33333 },
  { name: "Triplet quarter note", value: 6 },
  { name: "Eighth note", value: 8 },
  { name: "Quintuplet eighth note", value: 10 },
  { name: "Triplet eighth note", value: 12 },
  { name: "Sixteenth note", value: 16 },
  { name: "Quintuplet sixteenth note", value: 20 },
  { name: "Triplet sixteenth note", value: 24 },
];

/**
 * Equal-temperament chord definitions (intervals in semitones above the drone).
 * Each array does NOT include 0, because we add the fundamental ourselves.
 */
export const CHORDS_ET = {
  Major: [4, 7], // Quarter steps: e.g. C + E + G
  Minor: [3, 7],
  Dim: [3, 6],
  "Aug.": [4, 8],
  "Dom⁷": [4, 7, 10],
  "HalfDim⁷": [3, 6, 10],
  "FullDim⁷": [3, 6, 9],
  "Maj⁶": [4, 7, 9],
  "Min⁶": [3, 7, 9],
  "Dim⁶": [3, 6, 8],
  "Maj⁷": [4, 7, 11],
  "Min⁷": [3, 7, 10],
  "Dom⁷♯5": [4, 8, 10],
  "Min⁷♭5": [3, 6, 10],
  "Dom⁷♭5": [4, 6, 10],
  "MinMaj⁷": [3, 7, 11],
  "Maj⁷♯5": [4, 8, 11],
  "DimMaj⁷": [3, 6, 11],
  "Maj♭⁹": [4, 7, 13],
  "Min♭⁹": [3, 7, 13],
  "Maj+⁹": [4, 7, 15],
  "Min+⁹": [3, 7, 15],
  "Dom⁷+9": [4, 7, 10, 15],
  "Min⁷♭9": [3, 7, 10, 13],
  "Dom⁷♭9": [4, 7, 10, 13],
  "Maj⁷+9": [4, 7, 11, 15],
  Quartal: [5, 10],
  Quintal: [7, 14],
};

/**
 * Just Intonation chord definitions (offsets in *cents* from the fundamental).
 * Example: a "Major" triad => +386 cents, +702 cents for the 3rd & 5th.
 */
export const CHORDS_JI = {
  Major: [386, 702],
  Minor: [316, 702],
  Dim: [316, 568],
  "Aug.": [386, 816],
  "Dom⁷": [386, 702, 969],
  "HalfDim⁷": [316, 568, 969],
  "FullDim⁷": [316, 568, 814],
  "Maj⁶": [386, 702, 884],
  "Min⁶": [316, 702, 884],
  "Dim⁶": [316, 568, 772],
  "Maj⁷": [386, 702, 1088],
  "Min⁷": [316, 702, 968],
  "Dom⁷♯5": [386, 816, 968],
  "Min⁷♭5": [316, 568, 969],
  "Dom⁷♭5": [386, 583, 968],
  "MinMaj⁷": [332, 704, 1099],
  "Maj⁷♯5": [372, 812, 1114],
  "DimMaj⁷": [332, 551, 1104],
  "Maj♭⁹": [386, 702, 1305],
  "Min♭⁹": [316, 702, 1305],
  "Maj+⁹": [386, 702, 1404],
  "Min+⁹": [316, 702, 1404],
  "Dom⁷+9": [372, 704, 937, 1408],
  "Min⁷♭9": [332, 702, 968, 1305],
  "Dom⁷♭9": [372, 704, 937, 1305],
  "Maj⁷+9": [372, 704, 1076, 1408],
  Quartal: [498, 996],
  Quintal: [702, 1200],
};

/**
 * Just Intonation ratios for the 12 semitones
 * These are simple frequency ratios relative to the fundamental
 */
export const JI_SEMITONE_RATIOS = {
  0: { numerator: 1, denominator: 1 }, // Perfect unison
  1: { numerator: 16, denominator: 15 }, // Minor second
  2: { numerator: 9, denominator: 8 }, // Major second
  3: { numerator: 6, denominator: 5 }, // Minor third
  4: { numerator: 5, denominator: 4 }, // Major third
  5: { numerator: 4, denominator: 3 }, // Perfect fourth
  6: { numerator: 45, denominator: 32 }, // Augmented fourth
  7: { numerator: 3, denominator: 2 }, // Perfect fifth
  8: { numerator: 8, denominator: 5 }, // Minor sixth
  9: { numerator: 5, denominator: 3 }, // Major sixth
  10: { numerator: 9, denominator: 5 }, // Minor seventh
  11: { numerator: 15, denominator: 8 }, // Major seventh
};

export const INSTRUMENT_SAMPLES = {
  piano: {
    urls: {
      'A0': 'A0.mp3',
      'C1': 'C1.mp3',
      'D#1': 'Ds1.mp3',
      'F#1': 'Fs1.mp3',
      'A1': 'A1.mp3',
      'C2': 'C2.mp3',
      'D#2': 'Ds2.mp3',
      'F#2': 'Fs2.mp3',
      'A2': 'A2.mp3',
      'C3': 'C3.mp3',
      'D#3': 'Ds3.mp3',
      'F#3': 'Fs3.mp3',
      'A3': 'A3.mp3',
      'C4': 'C4.mp3',
      'D#4': 'Ds4.mp3',
      'F#4': 'Fs4.mp3',
      'A4': 'A4.mp3',
    },
    baseUrl: 'https://tonejs.github.io/audio/salamander/'
  },
  bass: {
    urls: {
      'C2': 'C2.mp3',
      'C3': 'C3.mp3',
      'C4': 'C4.mp3',
    },
    baseUrl: 'https://tonejs.github.io/audio/bass-electric/'
  }
};

// Extend your existing SOUND_OPTIONS
export const SOUND_OPTIONS = [
  {
    id: "clicker",
    name: "Clicker",
    type: "clicker",
    color: "gray-500",
  },
  {
    id: "click",
    name: "Click",
    frequency: 800,
    accentFrequency: 1000,
    type: "sine",
    color: "blue-500",
  },
  {
    id: "wood",
    name: "Wood Block",
    frequency: 1200,
    accentFrequency: 1800,
    type: "sine",
    color: "red-500",
  },
  {
    id: "cowbell",
    name: "Cowbell",
    frequency: 600,
    accentFrequency: 800,
    type: "triangle",
    color: "green-500",
  },
  {
    id: "hihat",
    name: "Hi-Hat",
    frequency: 2000,
    accentFrequency: 3000,
    type: "square",
    color: "yellow-500",
  },
  {
    id: "none",
    name: "No Sound",
    type: "none",
    color: "gray-300",
  },
];

/** Note options for drone pitch selection. */
export const NOTE_OPTIONS = [
  { label: "Off", value: null },
  { label: "C", value: 0 },
  { label: "C#/Db", value: 1 },
  { label: "D", value: 2 },
  { label: "D#/Eb", value: 3 },
  { label: "E", value: 4 },
  { label: "F", value: 5 },
  { label: "F#/Gb", value: 6 },
  { label: "G", value: 7 },
  { label: "G#/Ab", value: 8 },
  { label: "A", value: 9 },
  { label: "A#/Bb", value: 10 },
  { label: "B", value: 11 },
];

/** Remove octaves 0–3, only allow 4–6. */
export const OCTAVE_OPTIONS = [
  { label: "Octave 2", value: 2 },
  { label: "Octave 3", value: 3 },
  { label: "Octave 4", value: 4 },
  { label: "Octave 5", value: 5 },
  { label: "Octave 6", value: 6 },
];

export const NOTE_OPTIONS_12 = [
  { label: "C", value: 0 },
  { label: "C#/Db", value: 1 },
  { label: "D", value: 2 },
  { label: "D#/Eb", value: 3 },
  { label: "E", value: 4 },
  { label: "F", value: 5 },
  { label: "F#/Gb", value: 6 },
  { label: "G", value: 7 },
  { label: "G#/Ab", value: 8 },
  { label: "A", value: 9 },
  { label: "A#/Bb", value: 10 },
  { label: "B", value: 11 },
];

export const QUARTER_SHARP_OPTIONS = [
  { label: "C¼♯", value: 60.5 },
  { label: "D♭¼♯", value: 61.5 },
  { label: "D¼♯", value: 62.5 },
  { label: "E♭¼♯", value: 63.5 },
  { label: "E¼♯", value: 64.5 },
  { label: "F¼♯", value: 65.5 },
  { label: "G♭¼♯", value: 66.5 },
  { label: "G¼♯", value: 67.5 },
  { label: "A♭¼♯", value: 68.5 },
  { label: "A¼♯", value: 69.5 },
  { label: "B♭¼♯", value: 70.5 },
  { label: "B¼♯", value: 71.5 },
];

export const DYNAMICS_MAP = {
  pp: 0.2, // softest
  p: 0.63,
  mp: 1.0,
  mf: 1.45,
  f: 1.9,
  ff: 2.5, // loudest
};

export const NOTE_VALUE_OPTIONS = [
  { label: "Whole Note", factor: 4 },
  { label: "Dotted Half Note", factor: 3 },
  { label: "Half Note", factor: 2 },
  { label: "Dotted Quarter Note", factor: 1.5 },
  { label: "Quarter Note", factor: 1 },
  { label: "Dotted Eighth Note", factor: 0.75 },
  { label: "Eighth Note", factor: 0.5 },
  { label: "Dotted Sixteenth Note", factor: 0.375 },
  { label: "Sixteenth Note", factor: 0.25 },
  { label: "Dotted 32nd Note", factor: 0.1875 },
  { label: "32nd Note", factor: 0.125 },
];

export const validDenominators = [1, 2, 4, 8, 16, 32];

// The preset stress volumes by top number (ignoring denominator)
// Because all denominators 1,2,4,8,16,32 share the same pattern
export const STRESS_PATTERNS = {
  2: [1.0, 0.3],
  3: [1.0, 0.3, 0.3],
  4: [1.0, 0.3, 0.7, 0.3],
  6: [1.0, 0.3, 0.3, 0.7, 0.3, 0.3],
  8: [1.0, 0.3, 0.45, 0.3, 0.7, 0.3, 0.45, 0.3],
  9: [1.0, 0.3, 0.3, 1.0, 0.3, 0.3, 1.0, 0.3, 0.3],
  12: [1.0, 0.3, 0.3, 1.0, 0.3, 0.3, 1.0, 0.3, 0.3, 1.0, 0.3, 0.3],
  15: [
    1.0, 0.3, 0.3, 1.0, 0.3, 0.3, 1.0, 0.3, 0.3, 1.0, 0.3, 0.3, 1.0, 0.3, 0.3,
  ],
};

export const DEFAULT_CRESC_FACTOR = 4.0; // e.g. ramp up 50%
export const DEFAULT_DIM_FACTOR = 0.5; // e.g. ramp down 50%

export const DRONE_MIN_FREQ = 15;
export const HARMONY_MIN_FREQ = 250;
export const MAX_FREQ = 2000;

export const TEMPERAMENT_SYSTEMS = {
  "Equal Temperament": {
    description: "Standard 12-tone equal division of the octave",
    offsets: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    needsReference: false
  },
  "Just Intonation": {
    description: "Pure frequency ratios based on the harmonic series",
    // This will use the existing JI_SEMITONE_RATIOS implementation
    needsReference: true
  },
  "Pythagorean": {
    description: "Based on pure perfect fifths (3:2 ratio)",
    offsets: [0, -9.8, +3.9, -5.9, +7.8, -2.0, +11.7, +2.0, -7.8, +5.9, -3.9, +9.8],
    needsReference: true
  },
  "Meantone 1/4 Comma": {
    description: "1/4 comma meantone tuning with pure major thirds",
    offsets: [0, -24.0, -6.8, +10.3, -13.7, -27.4, -10.3, +6.8, -17.1, -0.0, -20.5, -3.4],
    needsReference: true
  },
  "Meantone 1/3 Comma": {
    description: "1/3 comma meantone temperament",
    offsets: [0, -18.8, -5.4, +8.2, -10.9, -21.7, -8.2, +5.4, -13.6, +0.0, -16.3, -2.7],
    needsReference: true
  },
  "Werckmeister III": {
    description: "Andreas Werckmeister's well temperament (1691)",
    offsets: [0, -9.8, -1.96, -7.82, -0.0, -5.87, -11.7, +1.96, -7.82, +3.91, -5.87, -13.69],
    needsReference: true
  },
  "Werckmeister IV": {
    description: "Werckmeister's fourth temperament",
    offsets: [0, -9.8, +3.9, -5.9, -1.96, -3.91, -9.8, +1.96, -7.8, -1.96, -11.7, -7.8],
    needsReference: true
  },
  "Werckmeister V": {
    description: "Werckmeister's fifth temperament",
    offsets: [0, -10.3, -3.4, -13.7, -6.8, -17.1, -10.3, -3.4, -13.7, -6.8, -17.1, -10.3],
    needsReference: true
  },
  "Kirnberger III": {
    description: "Johann Kirnberger's third temperament (c.1779)",
    offsets: [0, -6.84, +3.91, -3.91, -2.93, +1.96, -3.91, +0.0, -6.84, +3.91, -3.91, -2.93],
    needsReference: true
  },
  "Vallotti": {
    description: "Vallotti & Young temperament (c.1750)",
    offsets: [0, -5.9, -3.9, -2.0, -5.9, -3.9, -2.0, 0.0, -5.9, -3.9, -2.0, -5.9],
    needsReference: true
  },
  "Young's Well": {
    description: "Thomas Young's well temperament",
    offsets: [0, -5.9, -3.9, -2.0, 0.0, -3.9, -2.0, 0.0, -5.9, -3.9, -2.0, 0.0],
    needsReference: true
  },
  "Kellner Bach": {
    description: "Kellner's Bach temperament (1975)",
    offsets: [0, -4.89, -3.91, -0.98, -4.89, -1.96, -6.84, +0.98, -3.91, -1.96, -4.89, -2.93],
    needsReference: true
  }
};

/** 24-color palette for time signature colors - inspired by vintage music design */
export const TIME_SIGNATURE_COLORS = [
  // Neutrals and basics
  { name: "White", value: "bg-white", hex: "#ffffff" },
  { name: "Cream", value: "bg-amber-50", hex: "#fffbeb" },
  { name: "Light Beige", value: "bg-orange-100", hex: "#ffedd5" },
  { name: "Warm Beige", value: "bg-amber-100", hex: "#fef3c7" },
  
  // Deep Teals (primary signature color)
  { name: "Light Teal", value: "bg-teal-300", hex: "#5eead4" },
  { name: "Medium Teal", value: "bg-teal-500", hex: "#14b8a6" },
  { name: "Deep Teal", value: "bg-teal-700", hex: "#0f766e" },
  { name: "Dark Teal", value: "bg-emerald-800", hex: "#065f46" },
  
  // Warm Oranges (accent colors)
  { name: "Light Orange", value: "bg-orange-200", hex: "#fed7aa" },
  { name: "Warm Orange", value: "bg-orange-400", hex: "#fb923c" },
  { name: "Medium Orange", value: "bg-orange-500", hex: "#f97316" },
  { name: "Burnt Orange", value: "bg-orange-700", hex: "#c2410c" },
  
  // Golden Yellows
  { name: "Soft Yellow", value: "bg-yellow-200", hex: "#fef08a" },
  { name: "Golden Yellow", value: "bg-yellow-400", hex: "#facc15" },
  { name: "Amber Gold", value: "bg-amber-400", hex: "#fbbf24" },
  { name: "Deep Gold", value: "bg-amber-600", hex: "#d97706" },
  
  // Earth Tones
  { name: "Sage Green", value: "bg-green-600", hex: "#16a34a" },
  { name: "Forest Green", value: "bg-emerald-700", hex: "#047857" },
  { name: "Coral", value: "bg-red-400", hex: "#f87171" },
  { name: "Terra Cotta", value: "bg-red-600", hex: "#dc2626" },
  
  // Deep Accents
  { name: "Navy Blue", value: "bg-slate-800", hex: "#1e293b" },
  { name: "Charcoal", value: "bg-gray-700", hex: "#374151" },
  { name: "Deep Brown", value: "bg-amber-800", hex: "#92400e" },
  { name: "Burgundy", value: "bg-red-800", hex: "#991b1b" },
];

/** Common time signature presets */
export const COMMON_TIME_SIGNATURES = [
  { numerator: 2, denominator: 4, name: "2/4 (Simple Duple)" },
  { numerator: 3, denominator: 4, name: "3/4 (Simple Triple/Waltz)" },
  { numerator: 4, denominator: 4, name: "4/4 (Common Time)" },
  { numerator: 6, denominator: 8, name: "6/8 (Compound Duple)" },
  { numerator: 9, denominator: 8, name: "9/8 (Compound Triple)" },
  { numerator: 12, denominator: 8, name: "12/8 (Compound Quadruple)" },
  { numerator: 5, denominator: 4, name: "5/4 (Irregular)" },
  { numerator: 7, denominator: 8, name: "7/8 (Irregular)" },
  { numerator: 2, denominator: 2, name: "2/2 (Cut Time)" },
  { numerator: 3, denominator: 8, name: "3/8 (Simple Triple)" },
];

/** Default time signature colors - starts with white for all */
export const DEFAULT_TIME_SIGNATURE_COLORS = {
  "2/4": "bg-white",
  "3/4": "bg-white", 
  "4/4": "bg-white",
  "6/8": "bg-white",
  "9/8": "bg-white",
  "12/8": "bg-white",
  "5/4": "bg-white",
  "7/8": "bg-white",
  "2/2": "bg-white",
  "3/8": "bg-white",
};

export const DRONE_SOUND_OPTIONS = [
  {
    category: "Synthesis",
    sounds: [
      {
        id: "sine",
        name: "Sine Wave",
        type: "oscillator",
        oscillatorType: "sine",
      },
      {
        id: "triangle",
        name: "Triangle Wave", 
        type: "oscillator",
        oscillatorType: "triangle",
      },
      {
        id: "square",
        name: "Square Wave",
        type: "oscillator", 
        oscillatorType: "square",
      },
      {
        id: "sawtooth",
        name: "Sawtooth Wave",
        type: "oscillator",
        oscillatorType: "sawtooth",
      }
    ]
  },
  {
    category: "Instruments",
    sounds: [
      {
        id: "piano",
        name: "Piano",
        type: "sampler",
        instrument: "piano",
      },
      {
        id: "casio",
        name: "Casio",
        type: "sampler",
        instrument: "casio",
      },
        {
          id: "guitar", 
          name: "Guitar",
          type: "sampler",
          instrument: "guitar",
      },
       {
        id: "trombone",
        name: "Trombone",
        type: "sampler",
        instrument: "trombone",
      },
      //  {
      //    id: "flute",
      //    name: "Flute",
      //    type: "sampler",
      //    instrument: "flute",
      //  },
        {
          id: "tanpura",
          name: "Tanpura",
          type: "sampler",
          instrument: "tanpura",
        }
    ]
  }
];